<script setup lang="ts">
  import { useLocalizedPath } from '~/utils/locale'
  type NavItem = {
    name: string
    icon?: string
    path: string | { path: string }
    children?: NavItem[]
    isClickable?: boolean
    auth?: { roles: AuthRole[]; requiresAdminGroups?: boolean }
  }

  const props = defineProps<{
    item: NavItem
    iconOnly?: boolean
    selected?: boolean
    hasParent?: boolean
    bold?: boolean
    activeState?: 'active' | 'inactive'
  }>()

  const { t } = useI18n()
  const { getLocalizedPath, isActive } = useLocalizedPath()

  const emits = defineEmits(['clickArrow'])

  const authStore = useAuthStore()

  const isActiveItem = computed(() => {
    if (props.activeState) return props.activeState === 'active'
    if (isActive(props.item.path)) return true
    if (!props.item.children?.length) return false

    const hasActiveChild = (children: NavItem[]): boolean => {
      return children.some((child) => {
        if (isActive(child.path)) return true
        return child.children?.length ? hasActiveChild(child.children) : false
      })
    }

    return hasActiveChild(props.item.children)
  })
</script>

<template>
  <li v-if="authStore.isAuthorized(item.auth)">
    <NuxtLink
      :to="props.item.isClickable ? getLocalizedPath(props.item.path) : '#'"
      class="m-2 px-2 py-3 rounded text-base flex gap-2"
      :class="{
        'bg-primary text-white': isActiveItem,
        'hover:bg-gray-100 hover:text-black': !isActiveItem,
        'bg-gray-100 text-black': props.selected,
        'font-bold': !props.hasParent || !!props.bold,
        'cursor-pointer': props.item.isClickable,
      }"
    >
      <Icon
        v-if="props.item.icon"
        :name="props.item.icon"
        class="size-6 block min-w-6"
        :class="{
          'text-white': isActiveItem,
          'text-black': !isActiveItem,
        }"
      />
      <span
        class="transition-opacity duration-300 whitespace-nowrap flex-grow text-ellipsis overflow-hidden ... text-nowrap"
        :class="iconOnly ? 'opacity-0 invisible' : 'opacity-100'"
      >
        {{ t(`nav.menu.${props.item.name}`) }}
      </span>
      <Icon
        v-if="props.item.children"
        name="ic:baseline-chevron-right"
        class="transition-opacity size-6"
        :class="{
          'opacity-0 invisible': iconOnly,
          'opacity-100': !iconOnly,
          'text-white': isActiveItem,
          'text-black': !isActiveItem,
        }"
        @click="emits('clickArrow')"
      />
    </NuxtLink>
  </li>
</template>

<style scoped></style>
